import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Bug, Lightbulb, Send, CheckCircle, Home, Image, Info, FileText, MessageSquare } from 'lucide-react'

export default function FeedbackPage() {
  const [type, setType] = useState('bug') // 'bug' or 'feature'
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    // Enable scrolling (override global overflow:hidden)
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim() || !description.trim()) return
    
    setSubmitting(true)
    
    try {
      // Get device info for context
      const deviceInfo = {
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString()
      }
      
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          email: email.trim() || null,
          deviceInfo
        })
      })
      
      setSubmitted(true)
    } catch (error) {
      console.error('Feedback submission error:', error)
      // Still show success - we don't want to discourage feedback
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-ide-bg text-gray-100">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="bg-green-500/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-400" size={40} />
          </div>
          <h2 className="text-3xl font-bold mb-4">Thank You!</h2>
          <p className="text-gray-400 mb-8">
            Your {type === 'bug' ? 'bug report' : 'feature request'} has been submitted. 
            We appreciate you helping make P5 AI Studio better!
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/" className="px-6 py-3 bg-ide-accent hover:bg-ide-accentHover text-white rounded-lg transition-colors">
              Back to Editor
            </Link>
            <button 
              onClick={() => {
                setSubmitted(false)
                setTitle('')
                setDescription('')
              }}
              className="px-6 py-3 bg-ide-sidebar hover:bg-ide-hover text-white rounded-lg transition-colors"
            >
              Submit Another
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-ide-bg text-gray-100">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />
      
      <main id="main-content" className="max-w-2xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold mb-2">Send Feedback</h2>
        <p className="text-gray-400 mb-8">
          Help us improve P5 AI Studio by reporting bugs or suggesting new features.
        </p>

        {/* Type Selector */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setType('bug')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              type === 'bug' 
                ? 'border-red-500 bg-red-500/10' 
                : 'border-ide-border bg-ide-sidebar hover:border-gray-600'
            }`}
          >
            <Bug className={`mx-auto mb-2 ${type === 'bug' ? 'text-red-400' : 'text-gray-500'}`} size={32} />
            <div className={`font-semibold ${type === 'bug' ? 'text-red-400' : 'text-gray-400'}`}>
              Report a Bug
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Something isn't working right
            </div>
          </button>
          
          <button
            onClick={() => setType('feature')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              type === 'feature' 
                ? 'border-yellow-500 bg-yellow-500/10' 
                : 'border-ide-border bg-ide-sidebar hover:border-gray-600'
            }`}
          >
            <Lightbulb className={`mx-auto mb-2 ${type === 'feature' ? 'text-yellow-400' : 'text-gray-500'}`} size={32} />
            <div className={`font-semibold ${type === 'feature' ? 'text-yellow-400' : 'text-gray-400'}`}>
              Request a Feature
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Suggest an improvement
            </div>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">
              {type === 'bug' ? 'What went wrong?' : 'What would you like to see?'}
              <span className="text-red-400 ml-1">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={type === 'bug' ? 'e.g., AI response gets cut off' : 'e.g., Dark mode for preview'}
              className="w-full px-4 py-3 bg-ide-sidebar border border-ide-border rounded-lg focus:outline-none focus:border-ide-accent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Details
              <span className="text-red-400 ml-1">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={type === 'bug' 
                ? 'Please describe what happened, what you expected, and steps to reproduce...'
                : 'Describe your idea and how it would help you...'
              }
              rows={6}
              className="w-full px-4 py-3 bg-ide-sidebar border border-ide-border rounded-lg focus:outline-none focus:border-ide-accent resize-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Email <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 bg-ide-sidebar border border-ide-border rounded-lg focus:outline-none focus:border-ide-accent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Only if you'd like us to follow up with you
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting || !title.trim() || !description.trim()}
            className="w-full py-3 bg-ide-accent hover:bg-ide-accentHover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>Submitting...</>
            ) : (
              <>
                <Send size={18} />
                Submit {type === 'bug' ? 'Bug Report' : 'Feature Request'}
              </>
            )}
          </button>
        </form>

        {/* Alternative Contact */}
        <div className="mt-12 pt-8 border-t border-ide-border text-center">
          <p className="text-gray-500 text-sm mb-4">
            Prefer email? Reach us at{' '}
            <a href="mailto:feedback@p5js.ai" className="text-ide-accent hover:underline">
              feedback@p5js.ai
            </a>
          </p>
        </div>
      </main>
    </div>
  )
}

function Header() {
  return (
    <header className="border-b border-ide-border bg-ide-sidebar">
      <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <MessageSquare size={20} className="text-ide-accent" />
            Feedback
          </h1>
        </div>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
            <Home size={14} /> IDE
          </Link>
          <Link to="/gallery/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
            <Image size={14} /> Gallery
          </Link>
          <Link to="/about" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
            <Info size={14} /> About
          </Link>
          <Link to="/changelog" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
            <FileText size={14} /> Changelog
          </Link>
        </nav>
      </div>
    </header>
  )
}
